/**
 * EditionPickerStep - Edition phase of the Add to Collection modal
 *
 * Grid of all printings with a filter (set code, set name, collector number).
 * Clicking a tile selects it; arrow keys move between tiles.
 */
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { IoCheckmarkCircle } from 'react-icons/io5';

import CardImage from '@/components/CardImage/CardImage';
import Search from '@/components/Search/Search';
import SetIcon from '@/components/SetIcon/SetIcon';
import type { MinimalCard } from '@/services/cardDatabase';

import useSetInfoByCode from '../hooks/useSetInfoByCode';
import { filterPrintings, sortPrintingsByRelease } from '../lib/printingFilter';
import { collectionOwnedByScryfallIdAtom } from '../state/collectionAtom';

export interface EditionPickerStepProps {
  /** All printings of the selected card */
  printings: MinimalCard[];
  /** Printing selected earlier (highlighted) */
  selectedId?: string | null;
  onSelect: (printing: MinimalCard) => void;
}

/** Columns in the rendered grid, measured from tile positions */
const countColumns = (tiles: HTMLElement[]) => {
  const firstTop = tiles[0]?.offsetTop;
  const columns = tiles.findIndex((tile) => tile.offsetTop !== firstTop);
  return columns === -1 ? tiles.length : columns;
};

export default function EditionPickerStep({ printings, selectedId, onSelect }: EditionPickerStepProps): ReactElement {
  const setInfo = useSetInfoByCode();
  const ownedById = useAtomValue(collectionOwnedByScryfallIdAtom);
  const [filter, setFilter] = useState('');
  // Bumped to remount (and so clear) the uncontrolled filter input
  const [filterKey, setFilterKey] = useState(0);
  const filterRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const sortedPrintings = useMemo(() => sortPrintingsByRelease(printings, setInfo), [printings, setInfo]);
  const visiblePrintings = useMemo(() => filterPrintings(sortedPrintings, filter, setInfo), [sortedPrintings, filter, setInfo]);
  const ownedEditionCount = printings.filter((p) => ownedById.has(p.id)).length;

  const selectedIndex = visiblePrintings.findIndex((p) => p.id === selectedId);
  // Roving tabindex: one tile is tabbable, arrows move focus
  const [focusIndex, setFocusIndex] = useState(Math.max(selectedIndex, 0));
  const tabbableIndex = Math.min(focusIndex, visiblePrintings.length - 1);

  const getTiles = () => Array.from(gridRef.current?.querySelectorAll<HTMLElement>('[data-edition-tile]') ?? []);

  useEffect(() => {
    getTiles()[selectedIndex]?.scrollIntoView({ block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusTile = (index: number) => {
    const tiles = getTiles();
    const clamped = Math.max(0, Math.min(index, tiles.length - 1));
    setFocusIndex(clamped);
    tiles[clamped]?.focus();
  };

  const handleFilterKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusTile(filter ? 0 : Math.max(selectedIndex, 0));
    } else if (e.key === 'Enter' && visiblePrintings.length === 1) {
      e.preventDefault();
      onSelect(visiblePrintings[0]);
    }
  };

  const handleGridKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const tiles = getTiles();
    const current = tiles.indexOf(document.activeElement as HTMLElement);
    if (current === -1) return;
    const columns = countColumns(tiles);
    const moves: Record<string, number> = {
      ArrowRight: current + 1,
      ArrowLeft: current - 1,
      ArrowDown: current + columns,
      ArrowUp: current - columns,
      Home: 0,
      End: tiles.length - 1,
    };
    const target = moves[e.key];
    if (target === undefined) return;
    e.preventDefault();
    if (e.key === 'ArrowUp' && target < 0) {
      filterRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown' && target >= tiles.length) return;
    focusTile(target);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Search
          key={filterKey}
          ref={filterRef}
          onSearch={(value) => {
            if (value === filter) return;
            setFilter(value);
            setFocusIndex(0);
          }}
          onKeyDown={handleFilterKeyDown}
          debounceMs={120}
          placeholder="Filter by set name, set code or number (e.g. “znc 120”)"
          ariaLabel="Filter editions"
          className="flex-1"
          autoFocus
        />
        <div className="text-ash shrink-0 text-right text-xs" aria-live="polite">
          <p>
            <span className="text-silver font-medium">
              {filter ? `${visiblePrintings.length} of ${printings.length}` : printings.length}
            </span>{' '}
            editions
          </p>
          {ownedEditionCount > 0 && <p className="text-success">{ownedEditionCount} owned</p>}
        </div>
      </div>

      <div className="-mx-1 max-h-[62vh] min-h-75 overflow-y-auto px-1 py-1">
        {visiblePrintings.length === 0 ? (
          <div className="text-ash flex flex-col items-center justify-center gap-3 py-12 text-center">
            <p className="text-sm">No editions match “{filter}”</p>
            <button
              type="button"
              onClick={() => {
                setFilter('');
                setFilterKey((key) => key + 1);
              }}
              className="text-aether hover:text-pearl focus-ring rounded text-xs underline-offset-2 hover:underline"
            >
              Clear filter
            </button>
          </div>
        ) : (
          <div
            ref={gridRef}
            role="group"
            aria-label="Editions"
            onKeyDown={handleGridKeyDown}
            className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3"
          >
            {visiblePrintings.map((printing, index) => (
              <EditionTile
                key={printing.id}
                printing={printing}
                setName={setInfo.get(printing.set)?.name}
                iconSvgUri={setInfo.get(printing.set)?.iconSvgUri}
                ownedQuantity={ownedById.get(printing.id) ?? 0}
                isSelected={printing.id === selectedId}
                tabIndex={index === tabbableIndex ? 0 : -1}
                onFocus={() => setFocusIndex(index)}
                onSelect={() => onSelect(printing)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface EditionTileProps {
  printing: MinimalCard;
  setName?: string;
  iconSvgUri?: string;
  ownedQuantity: number;
  isSelected: boolean;
  tabIndex: number;
  onFocus: () => void;
  onSelect: () => void;
}

function EditionTile({
  printing,
  setName,
  iconSvgUri,
  ownedQuantity,
  isSelected,
  tabIndex,
  onFocus,
  onSelect,
}: EditionTileProps): ReactElement {
  const setCode = printing.set.toUpperCase();

  return (
    <button
      type="button"
      data-edition-tile
      tabIndex={tabIndex}
      onFocus={onFocus}
      onClick={onSelect}
      aria-label={`${setName ?? setCode} (${setCode}) #${printing.collector_number}${ownedQuantity > 0 ? `, owned ${ownedQuantity}` : ''}${isSelected ? ', previously selected' : ''}`}
      className={clsx(
        'group flex flex-col rounded-xl border p-2 text-left transition-all duration-150',
        'focus-visible:ring-aether focus-visible:ring-offset-obsidian focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        isSelected
          ? 'border-amber-300/80 bg-amber-300/10 shadow-[0_0_0_1px_rgba(252,211,77,0.5)]'
          : 'bg-slate/30 hover:bg-slate hover:border-silver/40 border-transparent hover:-translate-y-0.5 hover:shadow-lg',
      )}
    >
      <div className="relative">
        <CardImage
          imageUris={printing.image_uris}
          name={`${printing.name} (${setCode})`}
          size="fluid"
          className="transition-[filter] duration-150 group-hover:brightness-110"
        />
        {ownedQuantity > 0 && (
          <span className="bg-success/90 text-pearl absolute top-1 right-1 flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold shadow">
            <IoCheckmarkCircle className="h-3 w-3" aria-hidden="true" />
            {ownedQuantity}
          </span>
        )}
        {isSelected && (
          <span className="text-void absolute bottom-1 left-1 rounded-md bg-amber-300 px-1.5 py-0.5 text-[10px] font-bold shadow">
            Selected
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <SetIcon setCode={printing.set} rarity={printing.rarity} scryfallIconUri={iconSvgUri} size="sm" className="shrink-0" />
        <span className="text-pearl font-mono text-xs font-semibold">{setCode}</span>
        <span className="text-ash text-xs">#{printing.collector_number}</span>
      </div>
      <p className="text-silver mt-0.5 truncate text-[11px]" title={setName}>
        {setName ?? '\u00A0'}
      </p>
    </button>
  );
}
